import React, { useState, useEffect, useCallback } from "react";
import { v4 } from "uuid";
import Alerts from "../../molecules/Alerts";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTh, faList, faSearch } from "@fortawesome/free-solid-svg-icons";
import { getAccountsFromStatus } from "../../../utils/queries";
import { trackPromise } from "react-promise-tracker";
import { useNavigate } from "react-router-dom";
import { Input } from "reactstrap";
import "./HomePage.css";
import RequestCard, { getStatusBadge } from "../../organisms/RequestCard";

const Home = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [accountList, setAccountList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("viewMode") ?? "list");

  const addAlert = useCallback((message, color) => {
    setAlerts((prev) => [...prev, { message, id: v4(), color }]);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await trackPromise(getAccountsFromStatus("Validated by requestor"));
        setAccountList(result.data);
      } catch (error) {
        addAlert("Failed to load requests.", "error");
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [addAlert]);

  const filterLogic = (account) => {
    const searchNum = parseInt(nameFilter, 10);
    return isNaN(searchNum)
      ? !nameFilter ||
          account?.name?.toLowerCase().includes(nameFilter.toLowerCase()) ||
          account?.accountName?.toLowerCase().includes(nameFilter.toLowerCase()) ||
          account?.newAccountName?.toLowerCase().includes(nameFilter.toLowerCase()) ||
          account?.requestedByName?.toLowerCase().includes(nameFilter.toLowerCase()) ||
          account?.token?.toLowerCase() === nameFilter.toLowerCase()
      : Object.values(account).some((v) => String(v).includes(searchNum));
  };

  const filtered = accountList.filter(filterLogic).sort((a, b) => b.id - a.id);

  return (
    <div className="hy-page">
      <Alerts alerts={alerts} setAlerts={setAlerts} />

      {/* ── Toolbar ── */}
      <div className="hy-toolbar">
        <div className="hy-toolbar__left">
          <div className="hy-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="hy-search-icon" />
            <Input
              type="search"
              className="hy-search"
              placeholder="Search..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
        </div>
        <div className="hy-toolbar__right">
          <div className="hy-view-toggle">
            <button
              className={"hy-view-btn" + (viewMode === "card" ? " hy-view-btn--active" : "")}
              onClick={() => { setViewMode("card"); localStorage.setItem("viewMode", "card"); }}
              title="Card view"
            >
              <FontAwesomeIcon icon={faTh} />
            </button>
            <button
              className={"hy-view-btn" + (viewMode === "list" ? " hy-view-btn--active" : "")}
              onClick={() => { setViewMode("list"); localStorage.setItem("viewMode", "list"); }}
              title="List view"
            >
              <FontAwesomeIcon icon={faList} />
            </button>
          </div>
        </div>
      </div>

      {loaded && filtered.length === 0 ? (
        <div className="hy-empty">No pending approvals found</div>
      ) : viewMode === "card" ? (
        <div className="hy-card-grid">
          {filtered.map((account) => (
            <RequestCard
              key={account.id}
              account={account}
              href={`/requests/${account.id}`}
              onClick={() => navigate(`/requests/${account.id}`)}
            />
          ))}
        </div>
      ) : (
        <div className="hy-list">
          <div className="hy-list__header">
            <span className="hy-list__col hy-list__col--id">Id</span>
            <span className="hy-list__col hy-list__col--account">Account Name</span>
            <span className="hy-list__col hy-list__col--vendor">Vendor Name</span>
            <span className="hy-list__col hy-list__col--requestor">Requestor</span>
            <span className="hy-list__col hy-list__col--lm">Line Manager</span>
            <span className="hy-list__col hy-list__col--finance">Finance Approver</span>
            <span className="hy-list__col hy-list__col--status">Status</span>
            <span className="hy-list__col hy-list__col--date">Submitted</span>
          </div>
          {filtered.map((account) => (
            <a
              key={account.id}
              className="hy-list__row"
              href={`/requests/${account.id}`}
              onClick={(e) => { if (e.button === 0 && !e.ctrlKey && !e.metaKey) { e.preventDefault(); navigate(`/requests/${account.id}`); } }}
            >
              <span className="hy-list__col hy-list__col--id">{account.id}</span>
              <span className="hy-list__col hy-list__col--account">{account.accountName || account.newAccountName || "-"}</span>
              <span className="hy-list__col hy-list__col--vendor">{account.name || "-"}</span>
              <span className="hy-list__col hy-list__col--requestor">{account.requestedByName || "-"}</span>
              <span className="hy-list__col hy-list__col--lm">{account.lineManagerToNotify || "-"}</span>
              <span className="hy-list__col hy-list__col--finance">{account.financeApproverToNotify || "-"}</span>
              <span className="hy-list__col hy-list__col--status">{account.status || "-"}</span>
              <span className="hy-list__col hy-list__col--date">{account.formSubmittedDate ? moment(account.formSubmittedDate).format("DD-MM-YYYY HH:mm") : "-"}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default Home;

